import { FC } from 'react'
import { Button, Form } from '@wellesley/ui'
import { SubmitHandler } from 'react-hook-form'
import CommonDialog from 'components/modals/CommonDialog'
import zod from 'zod'
import { useCommonForm } from '@wellesley/forms'
import { Upload } from '@wellesley/openapi'
import { UploadItem } from '@wellesley/uploads'

import CommonFormField from '../../form/CommonFormField'

const MAX_ALT = 150

type CmpProps = {
  upload: Upload
  onClose: () => void
  onUpdateMedia: (upload: UploadItem, altText: string) => Promise<void>
}

type FormType = {
  altText: string
}

const UpdateMetaModal: FC<CmpProps> = ({ upload, onClose, onUpdateMedia }) => {
  const handleSubmit: SubmitHandler<FormType> = ({ altText }) => {
    void onUpdateMedia(upload as unknown as UploadItem, altText)
    onClose()
  }

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    form.reset()
    onClose()
  }

  const formSchema = zod.object({
    altText: zod.string().max(MAX_ALT, `Too long`),
  })

  const form = useCommonForm({
    schema: formSchema,
    defaultValues: {
      altText: upload.meta?.altText || '',
    },
  })

  const canSubmit =
    !form.getFieldState('altText').invalid && form.formState.isDirty

  return (
    <CommonDialog
      open={true}
      onCloseModal={onClose}
      className="max-w-[450px] -translate-y-[60px] rounded-sm shadow-[0px_0px_10px_0px_var(--alpha-black-10)] outline outline-1 outline-gray-200"
      data-testid="updateMetaModal"
      showClose={false}
      overlayClassName="hidden"
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Form {...form}>
          <form
            autoFocus={true}
            onSubmit={(...args) =>
              void form.handleSubmit(handleSubmit)(...args)
            }
          >
            <CommonFormField
              name="altText"
              displayName="Alternative text"
              placeholder="There is a picturesque landscape..."
              className="[&_label]:text-base [&_label]:font-normal [&_textarea]:h-[80px]"
              control={form.control}
              maxChars={MAX_ALT}
              autoFocus={true}
              isTextArea={true}
            />
          </form>
        </Form>
      </div>
      <div className="gap-xxs mt-xxs flex items-center justify-end">
        <Button variant="none" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!canSubmit}
          onClick={(e) => {
            e.stopPropagation()
            void form.handleSubmit(handleSubmit)()
          }}
        >
          Set ALT
        </Button>
      </div>
    </CommonDialog>
  )
}

export default UpdateMetaModal
